import { ReactNode, useMemo } from 'react';
import { generateHTML } from '@tiptap/core';

import {
  getExtensions,
  isRichtextEmpty,
} from 'approot/shared/richtext/richtext.utils';
import {
  RICHTEXT_SETTINGS,
  DiscussionProperties,
} from './discussion.constants';
import {
  BORDER_STYLE_BOX,
  BORDER_STYLE_SPEECH,
  BORDER_STYLE_THOUGHT,
} from 'global/constants';
import {
  InfoBoxStyleBoxSpeechInteractive,
  InfoBoxStyleThought,
} from '../info-box/info-box-view';
import { TTSComponentTrigger } from 'approot/activity-builder/activity-builder-zoom-component-modal';
import { COLOUR_PURPLE } from 'approot/shared/colour/colour.constants';
import { DiscussionViewAnswerIndicator } from './discussion-view.style';
import IconMessage from 'approot/shared/icons/icon-message';
import { useTTSContext } from 'approot/activity-builder/tts/tts.context';
import TTSButton from 'approot/activity-builder/tts/tts-button';
import TTSComponentMenu from 'approot/activity-builder/tts/tts-component-menu';

type Props = {
  id: string;
  type: string;
  hasAnswer?: boolean;
  extra?: ReactNode;
  componentLoading?: ReactNode;
  isZoomed?: boolean;
  isActive?: boolean;
  onClick?: () => void;
  onInstructionClick?: () => void;
};

const DiscussionViewQuestion = ({
  id,
  type,
  discussion,
  style,
  hasAnswer,
  extra,
  componentLoading,
  isZoomed,
  isActive,
  onClick,
  onInstructionClick,
}: Props & Omit<DiscussionProperties, '__typename' | 'answer'>) => {
  const { parsedText } = useTTSContext({ id });

  const showTTSButton = isZoomed || isActive;
  const isAudioPlaying = showTTSButton && parsedText;

  const question = useMemo(() => {
    // use richtext settings from edit mode
    const extensions = getExtensions(RICHTEXT_SETTINGS);
    return isAudioPlaying
      ? parsedText
      : isRichtextEmpty(discussion.instruction)
        ? ''
        : generateHTML(discussion.instruction, extensions);
  }, [discussion.instruction, isAudioPlaying, parsedText]);

  const componentId = showTTSButton ? id : undefined;
  if (!question) return null;

  const content = (
    <>
      {isZoomed && <TTSButton componentId={id} componentType={type} />}
      {onInstructionClick && (
        <TTSComponentTrigger isActive={isActive} onClick={onInstructionClick} />
      )}

      {(style?.border === BORDER_STYLE_SPEECH ||
        style?.border === BORDER_STYLE_BOX) && (
        <InfoBoxStyleBoxSpeechInteractive
          id={componentId}
          style={{ ...style, baseColour: COLOUR_PURPLE.base }}
          richtext={question}
          isZoomed={isZoomed}
        >
          {extra}
          {hasAnswer && (
            <DiscussionViewAnswerIndicator
              type="button"
              aria-haspopup={true}
              aria-label="Open discussion question"
              onClick={onClick}
            >
              <IconMessage width={24} height={24} />
            </DiscussionViewAnswerIndicator>
          )}
          {componentLoading}
        </InfoBoxStyleBoxSpeechInteractive>
      )}
      {style?.border === BORDER_STYLE_THOUGHT && (
        <InfoBoxStyleThought
          id={componentId}
          style={{ ...style, baseColour: COLOUR_PURPLE.base }}
          richtext={question}
          isZoomed={isZoomed}
        >
          {extra}
          {hasAnswer && (
            <DiscussionViewAnswerIndicator
              type="button"
              aria-haspopup={true}
              aria-label="Open discussion question"
              onClick={onClick}
            >
              <IconMessage width={24} height={24} />
            </DiscussionViewAnswerIndicator>
          )}
          {componentLoading}
        </InfoBoxStyleThought>
      )}
    </>
  );

  return onInstructionClick ? (
    <TTSComponentMenu
      isActive={!!isActive}
      componentId={id}
      componentType={type}
    >
      {content}
    </TTSComponentMenu>
  ) : (
    content
  );
};

export default DiscussionViewQuestion;
